// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Icon, makeStyles } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies

import OverLoading from '../global/OverLoading';
import { updateModalContent, toggleModal } from '../../actions/modal';
import { IsArray, IsEmpty, IsTrue } from '../../common/checks';
import { Status } from '../../common/constants';
import MText from '../common/MText';
import MButton from '../common/MButton';
import Colours from '../../styles/colours';
import apiService from '../../services.js/apiService';
import { formatDateToDayMonthNameYear, showDateInSGTime } from '../../common/util';

const useStyles = makeStyles(() => ({
  iconWrapper: {
    width:          24,
    height:         24,
    display:        'flex',
    justifyContent: 'center',
    alignItems:     'center',
  },
}));

const initializeFocusValue = {
  nameFocus:  false,
  poiIdFocus: false,
  typeFocus:  false,
};

const POITable = (props) => {
  const classes = useStyles();

  const table = useRef(null);
  const columns = [
    { title: 'Id', field: 'id', filtering: false, width: '5%' },
    {
      title:     'Name',
      field:     'name',
      filtering: false,
      render:    (rowData) => <MButton text={rowData.name} variant="text" textColour={Colours.Blue} onClick={() => window.open(`/admin/poi/${rowData.id}`, '_blank')} />,
    },
    {
      title:     'Description',
      field:     'description',
      filtering: false,
      render:    (poi) => {
        const maxLength = 50;
        if (!IsEmpty(poi.description)) {
          return (poi.description.length >= maxLength) ? `${poi.description.substring(0, maxLength)}...` : poi.description;
        }
      },
    },
    // { title: 'Type', field: 'type', filtering: false },
    {
      title:     'Information',
      field:     'title',
      filtering: false,
      width:     '100%',
      render:    (rowData) => (
        <div className="d-flex flex-column justify-content-center" style={{ width: 150, gap: 5 }}>
          {rowData.address && (
            <div className="d-flex align-items-center w-100" style={{ gap: 5 }}>
              <Icon className={`fa fa-home ${classes.iconWrapper}`} />
              <MText variant="span">
                {rowData.address}
              </MText>
            </div>
          )}
          {rowData.tel && (
            <div className="d-flex align-items-center w-100" style={{ gap: 5 }}>
              <Icon className={`fa fa-phone ${classes.iconWrapper}`} />
              <MText variant="span">
                {rowData.tel}
              </MText>
            </div>
          )}
          {rowData.website && (
            <div className="d-flex align-items-center w-100" style={{ gap: 5 }}>
              <Icon className={`fa fa-globe ${classes.iconWrapper}`} />
              <MText variant="span">
                {rowData.website}
              </MText>
            </div>
          )}
          {(rowData.latc || rowData.lng) && (
            <div className="d-flex align-items-center w-100" style={{ gap: 5 }}>
              <Icon className={`fa fa-map-marker ${classes.iconWrapper}`} />
              <MText variant="span">
                {`${rowData.lat}, ${rowData.lng}`}
              </MText>
            </div>
          )}
        </div>
      ),
    },
    {
      title:     'Opening Hours',
      field:     'openingHours',
      filtering: false,
      render:    (rowData) => (
        <div style={{ width: 150 }}>
          {rowData.openingHours ? rowData.openingHours.default.map((openingHour, index) => (openingHour.hours.length ? (
            <div key={`${JSON.stringify(openingHour)}-${index}`} className="d-flex flex-column gap-2">
              <MText className="d-block" bold size={12} text={openingHour.day} />
              {openingHour.hours.map((hour, idx) => (
                <div key={`${JSON.stringify(hour)}-${idx}`} className="d-flex align-items-center gap-2">
                  <MText className="d-block" text={hour.from} size={12} width={100} />
                  &nbsp;
                  -
                  &nbsp;
                  <MText className="d-block" text={hour.to} size={12} />
                </div>
              ))}
            </div>
          ) : <></>)) : <>-</>}
        </div>
      ),
    },
    {
      title:     'Media',
      field:     'media',
      filtering: false,
      width:     '50%',
      render:    (rowData) => (
        <div className="d-flex align-items-center gap-5" style={{ width: 100 }}>
          {rowData.media
            && (
              <img
                src={rowData.media}
                alt=""
                style={{ width: 100, height: 100, borderRadius: 5, flexShrink: 0, flexGrow: 0, flex: 'none', objectFit: 'contain' }}
              />
            )}
        </div>
      ),
    },
    {
      title:     'Rating',
      field:     'rating',
      filtering: false,
      render:    (rowData) => (
        <div className="d-flex align-items-center gap-5">
          {rowData.rating
            && (
              <>
                <div className="font-weight-bold">{rowData.rating}</div>
              </>
            )}
        </div>
      ),
    },
    { title: 'Review Count', field: 'reviewCount', filtering: false },
    { title: 'Status', field: 'status', filtering: false },
    {
      title:  'Created At',
      field:  'createdAt',
      render: (rowData) => (rowData.createdAt
        ? formatDateToDayMonthNameYear(showDateInSGTime(rowData.createdAt), true)
        : '-'),
    },
    {
      title:  'Last Update',
      field:  'updatedAt',
      render: (rowData) => (rowData.updatedAt
        ? formatDateToDayMonthNameYear(showDateInSGTime(rowData.updatedAt), true)
        : '-'),
    },
    { title: 'Updated By', field: 'lastUpdatedBy', filtering: false },
  ];

  const [pageSize, setPageSize] = useState(20);
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { nameFocus, poiIdFocus } = autoFocus;

  const [triggerClearFilters, setTriggerClearFilters] = useState(false);
  const [poiId, setPoiId] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [wheelchairFriendly, setWheelchairFriendly] = useState('');
  const [petFriendly, setPetFriendly] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchDataTable = (query) => new Promise(async (resolve) => {
    try {
      setLoading(true);
      query.id = poiId;
      query.status = status;
      query.name = name;
      query.type = type;
      query.isWheelChairAcc = wheelchairFriendly ? (wheelchairFriendly === 'true') : null;
      query.isPetFriendly = petFriendly ? (petFriendly === 'true') : null;

      // SET DEFAULT SORT BY ID
      query.orderBy = 'id';
      query.orderDirection = 'ASC';

      const result = await apiService.getPois(query);

      let { rows, totalCount } = result;
      let { page } = query;

      if (rows.length === 0) {
        page = 0;

        if (totalCount !== 0) {
          const resultFirstPage = await apiService.getPois(
            { ...query, page },
          ).then((res) => {
            setLoading(false);
            return res;
          });

          rows = resultFirstPage.rows;
        }
      }

      // take the first media from the media object and if there is no media, then return the media empty object
      rows = rows.map((row) => {
        if (row.photos && IsArray(row.photos) && !IsEmpty(row.photos)) {
          const foundPhoto = row.photos[0];
          return {
            ...row,
            media: foundPhoto.crop_url || foundPhoto.url || '',
          };
        }

        return { ...row, media: '' };
      });

      resolve({
        data: rows,
        page,
        totalCount,
      });
    } catch (err) {
      toast.error(err.message);
      resolve({
        data:       [],
        page:       0,
        totalCount: 0,
      });
    } finally {
      setLoading(false);
    }
  });

  function handleSearch(fieldName, setStateFunction, event) {
    setStateFunction(event.target.value);
    setAutoFocus({ [`${fieldName}Focus`]: true });
  }

  const handleChange = (event) => {
    if (event.target.name === 'PoiStatus') {
      setStatus(event.target.value);
    }

    if (event.target.name === 'PoiWheelchairFriendly') {
      setWheelchairFriendly(event.target.value);
    }

    if (event.target.name === 'PoiPetFriendly') {
      setPetFriendly(event.target.value);
    }
  };

  const handleDelete = (id) => {
    props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this POI will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setIsDeleting(true);
            const result = await apiService.deletePoi(id);
            toast.success('POI deleted.');
            if (result) {
              table.current.onQueryChange();
            }
          } catch (err) {
            toast.error(err.message);
          } finally {
            setIsDeleting(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const clearFilters = () => {
    setPoiId('');
    setName('');
    setStatus('');
    setType('');
    setWheelchairFriendly('');
    setPetFriendly('');

    setTriggerClearFilters(true);
  };

  useEffect(() => {
    if (IsTrue(triggerClearFilters)) {
      table.current.onQueryChange();
      setTriggerClearFilters(false);
    }
  }, [triggerClearFilters]);

  return (
    <div>
      {isDeleting && <OverLoading />}
      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        onChangeRowsPerPage={(size) => {
          setAutoFocus(initializeFocusValue);
          setPageSize(size);
        }}
        isLoading={loading}
        data={fetchDataTable}
        actions={[
          {
            icon:    'edit',
            tooltip: 'Edit Poi',
            onClick: (event, poi) => {
              window.open(`/admin/poi/${poi.id}`, '_blank');
            },
          },
          {
            icon:    'delete',
            tooltip: 'Delete Poi',
            onClick: (event, poi) => {
              handleDelete(poi.id);
            },
          },
        ]}
        options={{
          pageSize,
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
        }}
        components={{
          Toolbar: () => (
            <div className="pt-3">
              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="17%">
                  <FormControl fullWidth>
                    <MButton
                      variant="outlined"
                      size="medium"
                      textColour={Colours.Primary}
                      onClick={() => { clearFilters(); }}
                      text="Clear Filters"
                    />
                  </FormControl>
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
                <Box p={1} width="10%">
                  <FormControl fullWidth>
                    <TextField
                      label="Id"
                      type="number"
                      value={poiId || ''}
                      onChange={(e) => handleSearch('poiId', setPoiId, e)}
                      autoFocus={poiIdFocus}
                    />
                  </FormControl>
                </Box>

                <Box p={1} width="20%">
                  <FormControl fullWidth>
                    <TextField
                      label="Name"
                      value={name || ''}
                      onChange={(e) => handleSearch('name', setName, e)}
                      autoFocus={nameFocus}
                    />
                  </FormControl>
                </Box>

                {/* <Box p={1} width="17%">
                  <FormControl fullWidth>
                    <TextField
                      label="Type"
                      value={type || ''}
                      onChange={(e) => handleSearch('type', setType, e)}
                      autoFocus={typeFocus}
                    />
                  </FormControl>
                </Box> */}

                <Box p={1} width="15%">
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      value={status}
                      name="PoiStatus"
                      onChange={handleChange}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
                      <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
                      <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box p={1} width="17%">
                  <FormControl fullWidth>
                    <MButton
                      variant="contained"
                      className="mt-3"
                      size="medium"
                      colour={Colours.Primary}
                      textColour={Colours.White}
                      onClick={() => { table.current.onQueryChange(); }}
                      text="Search"
                    />
                  </FormControl>
                </Box>

              </Box>
            </div>
          ),
        }}
      />
    </div>
  );
};

POITable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

// map the redux action creators to props
const mapActionToProps = {
  updateModalContent,
  toggleModal,
};

// connect(mapStateToProps, actions to component as props)(component)
export default connect(null, mapActionToProps)(POITable);
